// [2026-09-04] ADDED — the line-icon set for the revamped customer shell.
//
// WHY THESE ARE HAND-WRITTEN SVG AND NOT AN ICON PACKAGE
// -----------------------------------------------------
// Adding a dependency (lucide-react, react-icons) to draw twenty glyphs would put a package in the
// bundle of BOTH apps — proctoring-frontend-admin compiles against this package and would inherit
// it — for something that is eighteen path elements. These are inline, tree-shaken by definition,
// and cost no install.
//
// EVERY ICON IS `aria-hidden`. Not one of them is the only carrier of a meaning: each sits beside a
// text label (a tile caption, a button word, a table header). That is the same rule theme.css
// records for the severity badges — an interface whose meaning depends on recognising a glyph is an
// interface some of its readers cannot use. If you ever add an icon-ONLY control, give the BUTTON an
// aria-label; do not make the icon itself speak.
//
// All of them inherit `currentColor` and size from CSS, so a caller changes colour by setting
// `color` on the parent rather than by passing a prop that then has to be threaded everywhere.

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
}

const Icon = ({ children, ...rest }) => <svg {...BASE} {...rest}>{children}</svg>

// ── shell chrome ──────────────────────────────────────────────────────────────

export const GridIcon = (p) => (
  <Icon {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </Icon>
)

export const ChevronRightIcon = (p) => <Icon {...p}><path d="M9 18l6-6-6-6" /></Icon>
export const ChevronLeftIcon = (p) => <Icon {...p}><path d="M15 18l-6-6 6-6" /></Icon>

export const BellIcon = (p) => (
  <Icon {...p}>
    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 01-3.4 0" />
  </Icon>
)

export const PlusIcon = (p) => <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>

export const SignOutIcon = (p) => (
  <Icon {...p}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </Icon>
)

// ── KPI tiles ─────────────────────────────────────────────────────────────────

export const CardIcon = (p) => (
  <Icon {...p}>
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
  </Icon>
)

export const TrendingUpIcon = (p) => (
  <Icon {...p}><path d="M22 7l-8.5 8.5-5-5L2 17" /><path d="M16 7h6v6" /></Icon>
)

export const ZapIcon = (p) => <Icon {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></Icon>

export const CalendarIcon = (p) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="18" rx="2.5" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Icon>
)

export const UsersIcon = (p) => (
  <Icon {...p}>
    <path d="M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="9.5" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </Icon>
)

export const MessageIcon = (p) => (
  <Icon {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></Icon>
)

export const CheckCircleIcon = (p) => (
  <Icon {...p}>
    <path d="M22 11.1V12a10 10 0 11-5.93-9.14" />
    <path d="M22 4L12 14.01l-3-3" />
  </Icon>
)

export const BriefcaseIcon = (p) => (
  <Icon {...p}>
    <rect x="2" y="7" width="20" height="14" rx="2.5" />
    <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
  </Icon>
)

// ── table toolbar & row actions ───────────────────────────────────────────────

export const SearchIcon = (p) => (
  <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.1-4.1" /></Icon>
)

export const RefreshIcon = (p) => (
  <Icon {...p}>
    <path d="M3 2v6h6" />
    <path d="M3.5 13a9 9 0 105.6-8.4L3 8" />
  </Icon>
)

export const FilterIcon = (p) => (
  <Icon {...p}><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></Icon>
)

export const DownloadIcon = (p) => (
  <Icon {...p}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5M12 15V3" />
  </Icon>
)

export const RotateIcon = (p) => (
  <Icon {...p}>
    <path d="M21 2v6h-6" />
    <path d="M20.5 13a9 9 0 11-2.6-6.4L21 8" />
  </Icon>
)

export const MinusCircleIcon = (p) => (
  <Icon {...p}><circle cx="12" cy="12" r="9.5" /><path d="M8 12h8" /></Icon>
)

export const TrashIcon = (p) => (
  <Icon {...p}>
    <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </Icon>
)

export const CopyIcon = (p) => (
  <Icon {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </Icon>
)

export const ReportIcon = (p) => (
  <Icon {...p}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6M9 13h6M9 17h4" />
  </Icon>
)

// [2026-09-05] ADDED — for the session report: the per-screenshot "view" control, the captured-at
// timestamp, and the section headers in the IncProctor report design.
export const EyeIcon = (p) => (
  <Icon {...p}>
    <path d="M2 12s3.8-7 10-7 10 7 10 7-3.8 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3.2" />
  </Icon>
)

export const ClockIcon = (p) => (
  <Icon {...p}><circle cx="12" cy="12" r="9.5" /><path d="M12 6.8V12l3.4 2" /></Icon>
)

export const ListIcon = (p) => (
  <Icon {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2.5" />
    <path d="M9 3v18M3 9h18" />
  </Icon>
)

export const MonitorIcon = (p) => (
  <Icon {...p}>
    <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
    <path d="M8 21h8M12 17v4" />
  </Icon>
)

export const CreditsIcon = (p) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M14.5 9.2a3 3 0 100 5.6M9.5 10.7h4M9.5 13.3h4" />
  </Icon>
)

// [2026-09-16] ADDED — the three device glyphs for the session report's Device Information card.
//
// Hand-written for the reason at the top of this file: the design they come from
// (frontend-feedback's ProctoringLogsView) draws them with lucide-react, and importing that package
// here would land it in the admin app's bundle too. Same three shapes, no dependency.
//
// NOTHING IS REPLACED. MonitorIcon above is untouched and still used by the screenshots card.

export const CameraIcon = (p) => (
  <Icon {...p}>
    <path d="M3 8.5a2 2 0 012-2h1.6a1.6 1.6 0 001.35-.75l.7-1.1A1.6 1.6 0 0111 3.9h2a1.6 1.6 0 011.35.75l.7 1.1a1.6 1.6 0 001.35.75H19a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <circle cx="12" cy="12.5" r="3.2" />
  </Icon>
)

export const MicIcon = (p) => (
  <Icon {...p}>
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21M9 21h6" />
  </Icon>
)

export const SpeakerIcon = (p) => (
  <Icon {...p}>
    <path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z" />
    <path d="M15.8 9.3a3.8 3.8 0 010 5.4M18.4 6.8a7.3 7.3 0 010 10.4" />
  </Icon>
)
